import type { Ref } from 'vue';
import { ref, computed, watch } from 'vue';
import type { ReportStatItem } from '@/composables/useReportsPayments';
import {
    normalizePaymentsChartRange,
    rangeIntersectsCurrentChartPeriod,
    chartBucketRange,
} from '@/utils/chartRangeNormalizer';
import { externalTooltip, bucketTitle } from '@/utils/chartTooltip';
import { formatChartLabel } from '@/utils/dt';
import { useTimezoneStore } from '@/stores/timezone';
import {
    getAccountsReportChart,
    getAccountsReportStats,
    type AccountsReportChart,
    type AccountsReportMetricKey,
} from '@/services/reportsService';

type ViewMode = 'days' | 'weeks' | 'months';

interface CategoryEntry {
    labels: string[];
    values: number[];
}

type CategoryMap = Record<string, CategoryEntry>;

const DATASET_BASE = Object.freeze({
    backgroundColor: '#FC7C18',
    borderRadius: { topLeft: 12, topRight: 12 },
    borderSkipped: 'bottom' as const,
    maxBarThickness: 80,
});

const SERIES_LABEL: Record<AccountsReportMetricKey, string> = {
    validated: 'Validated',
    'pending-validation': 'Pending Validation',
    delinquent: 'Delinquent',
    'unsettled-accounts': 'Unsettled Accounts',
};

const STAT_DEFINITIONS: Array<{ id: AccountsReportMetricKey; label: string; icon: string }> = [
    { id: 'validated', label: 'Validated Accounts', icon: 'fa-solid fa-person-circle-check' },
    { id: 'pending-validation', label: 'Accounts Pending Validation', icon: 'fa-solid fa-person-circle-exclamation' },
    { id: 'delinquent', label: 'Delinquent Accounts', icon: 'fa-solid fa-person-circle-minus' },
    { id: 'unsettled-accounts', label: 'Unsettled Accounts', icon: 'fa-solid fa-person-circle-question' },
];

function emptyCategoryData(): CategoryMap {
    return {
        validated: { labels: [], values: [] },
        'pending-validation': { labels: [], values: [] },
        delinquent: { labels: [], values: [] },
        'unsettled-accounts': { labels: [], values: [] },
    };
}

function emptyMetricValues(): Record<string, number> {
    return {
        validated: 0,
        'pending-validation': 0,
        delinquent: 0,
        'unsettled-accounts': 0,
    };
}

function formatLabelsForView(labels: string[], mode: ViewMode): string[] {
    if (mode !== 'months') {
        return labels;
    }

    return labels.map((label) => {
        const slashMatch = label.match(/^(\d{1,2})\/(\d{4})$/);
        if (slashMatch) {
            const [, month, year] = slashMatch;
            return formatChartLabel(`${year}-${month.padStart(2, '0')}-01`, mode);
        }

        const dashMatch = label.match(/^(\d{4})-(\d{1,2})$/);
        if (dashMatch) {
            const [, year, month] = dashMatch;
            return formatChartLabel(`${year}-${month.padStart(2, '0')}-01`, mode);
        }

        return label;
    });
}

function mapChartToCategoryData(chart: AccountsReportChart, mode: ViewMode): CategoryMap {
    const labels = formatLabelsForView(chart.labels, mode);

    return {
        validated: { labels, values: chart.series.validated },
        'pending-validation': { labels, values: chart.series['pending-validation'] },
        delinquent: { labels, values: chart.series.delinquent },
        'unsettled-accounts': { labels, values: chart.series['unsettled-accounts'] },
    };
}

export function useReportsAccounts(dateRange: Ref<[string, string] | null>) {
    const viewMode = ref<ViewMode>('days');
    const activeStatId = ref<string | null>(null);
    const tzStore = useTimezoneStore();
    const loading = ref(true);
    const reportStats = ref<Record<string, number>>(emptyMetricValues());
    const dailyAverages = ref<Record<string, number>>(emptyMetricValues());
    const categoryData = ref<CategoryMap>(emptyCategoryData());

    const chartRange = ref<{ from: string; to: string }>({ from: '', to: '' });

    const stats = computed<ReportStatItem[]>(() =>
        STAT_DEFINITIONS.map((def) => ({
            ...def,
            value: (reportStats.value[def.id] ?? 0).toLocaleString('en-US'),
        })),
    );

    const chartData = computed(() => {
        const cats = categoryData.value;
        const labels = cats.validated.labels;
        const activeId = activeStatId.value;

        if (activeId && cats[activeId]) {
            return {
                labels,
                datasets: [{ ...DATASET_BASE, data: cats[activeId].values }],
            };
        }

        return {
            labels,
            datasets: STAT_DEFINITIONS.map((def) => ({
                ...DATASET_BASE,
                data: cats[def.id]?.values ?? [],
            })),
        };
    });

    const dailyAverage = computed(() => {
        const activeId = activeStatId.value;

        if (activeId && dailyAverages.value[activeId] !== undefined) {
            return Math.round(dailyAverages.value[activeId]).toLocaleString('en-US');
        }

        const avg = STAT_DEFINITIONS.reduce((sum, def) => sum + (dailyAverages.value[def.id] ?? 0), 0);
        return Math.round(avg).toLocaleString('en-US');
    });

    const chartOptions = computed(() => ({
        interaction: { mode: 'index' as const, intersect: true },

        plugins: {
            legend: { display: false },
            tooltip: externalTooltip({
                title: (items: Array<{ dataIndex: number; label: string }>) => {
                    const item = items[0];
                    if (!item) return '';
                    const { from, to } = chartRange.value;
                    const bucket = from && to
                        ? chartBucketRange(from, to, viewMode.value, item.dataIndex)
                        : null;
                    return bucketTitle(item.label, viewMode.value, bucket);
                },
                label: (ctx: { datasetIndex: number; parsed: { y: number } }) => {
                    const key = (activeStatId.value as AccountsReportMetricKey | null)
                        ?? STAT_DEFINITIONS[ctx.datasetIndex]?.id;
                    const name = key ? SERIES_LABEL[key] : 'Accounts';
                    return `${name}: ${(ctx.parsed.y ?? 0).toLocaleString('en-US')}`;
                },
            }),
        },
    }));

    const rangeIncludesCurrentPeriod = computed(() => {
        const range = dateRange.value;
        if (!range) return false;
        const [start, end] = range;
        return rangeIntersectsCurrentChartPeriod(start, end, viewMode.value, tzStore.effectiveTimezone);
    });

    const fetchData = async (onCleanup: (cleanupFn: () => void) => void) => {
        const range = dateRange.value;
        if (!range) {
            loading.value = true;
            return;
        }

        let isCurrent = true;
        onCleanup(() => { isCurrent = false; });

        loading.value = true;
        const [startDate, endDate] = range;
        const currentViewMode = viewMode.value;
        const timezone = tzStore.effectiveTimezone;
        const normalized = normalizePaymentsChartRange(startDate, endDate, currentViewMode, 20, timezone);

        try {
            const [statsResponse, chartResponse] = await Promise.all([
                getAccountsReportStats(startDate, endDate),
                getAccountsReportChart(normalized.from, normalized.to, currentViewMode),
            ]);

            if (!isCurrent) return;

            chartRange.value = normalized;
            reportStats.value = statsResponse;
            dailyAverages.value = chartResponse.dailyAverages;
            categoryData.value = mapChartToCategoryData(chartResponse, currentViewMode);
        } catch (error) {
            if (isCurrent) {
                console.error(error);
            }
        } finally {
            if (isCurrent) {
                loading.value = false;
            }
        }
    };

    watch([dateRange, viewMode, () => tzStore.effectiveTimezone], (_value, _oldValue, onCleanup) => {
        void fetchData(onCleanup);
    }, { immediate: true });

    return {
        viewMode,
        activeStatId,
        stats,
        chartData,
        chartOptions,
        dailyAverage,
        loading,
        categoryData,
        rangeIncludesCurrentPeriod,
    };
}
